import { DogFormData } from '@/types/dog';
import { dogService } from '@/services/firebase/dogService';

/**
 * フォームの祖父母セレクタ値（ID または `__new__:名前`）。
 * 父犬・母犬と同じ表現を踏襲する。
 */
export interface GrandparentSelectorValues {
  /** 父方祖父（父犬の fatherId に紐付け） */
  paternalGrandfatherId?: string;
  /** 父方祖母（父犬の motherId に紐付け） */
  paternalGrandmotherId?: string;
  /** 母方祖父（母犬の fatherId に紐付け） */
  maternalGrandfatherId?: string;
  /** 母方祖母（母犬の motherId に紐付け） */
  maternalGrandmotherId?: string;
}

/**
 * 祖父母セレクタ値を実 ID に解決する。
 * - 空 → undefined（紐付けなし）
 * - `__new__:名前` → 最小フィールドで Dog を新規作成して ID を返す
 *   （gender は祖父=オス / 祖母=メス）
 * - 既存 ID → そのまま返す
 */
async function resolveSelectorValue(
  value: string | undefined,
  gender: 'male' | 'female',
  userId: string
): Promise<string | undefined> {
  if (!value) {
    return undefined;
  }

  if (value.startsWith('__new__:')) {
    const name = value.replace('__new__:', '');
    const created = await dogService.createDog(
      {
        name: name || '（名称不明）',
        gender,
        birthDate: new Date(),
        status: 'active',
        bioNumber: '',
        officialName: name || undefined,
      },
      userId
    );
    return created.id;
  }

  return value;
}

/**
 * 親犬に祖父母を紐付ける。既存の親情報（fatherId/motherId）は上書きしない。
 */
async function linkGrandparents(
  parentId: string | undefined,
  grandfatherValue: string | undefined,
  grandmotherValue: string | undefined,
  userId: string
): Promise<void> {
  // 親 ID が未確定（__new__）や未指定なら紐付け先が無いためスキップ
  if (!parentId || parentId.startsWith('__new__:')) {
    return;
  }
  // 紐付ける祖父母セレクタ値が無ければ何もしない
  if (!grandfatherValue && !grandmotherValue) {
    return;
  }

  const grandfatherId = await resolveSelectorValue(grandfatherValue, 'male', userId);
  const grandmotherId = await resolveSelectorValue(grandmotherValue, 'female', userId);
  if (!grandfatherId && !grandmotherId) {
    return;
  }

  const parent = await dogService.getDog(parentId, userId);
  if (!parent) {
    return;
  }

  const update: Partial<DogFormData> = {};
  if (grandfatherId && !parent.fatherId) update.fatherId = grandfatherId;
  if (grandmotherId && !parent.motherId) update.motherId = grandmotherId;

  if (Object.keys(update).length > 0) {
    await dogService.updateDog(parentId, update, userId);
  }
}

export interface RegisterGrandparentsParams {
  /** 解決済みの父犬 ID（父方祖父母の紐付け先） */
  fatherId?: string;
  /** 解決済みの母犬 ID（母方祖父母の紐付け先） */
  motherId?: string;
  /** フォームの祖父母セレクタ値（ID または `__new__:名前`） */
  grandparents: GrandparentSelectorValues;
  userId: string;
}

/**
 * フォームの祖父母セレクタ値を実 ID に解決し、
 * 父犬・母犬の fatherId/motherId に紐付ける。
 *
 * 手動編集された祖父母も反映できるよう、AI の raw データではなく
 * フォーム値を起点とする。これにより血統図（PedigreeTree）が常に
 * 3 世代分復元できる状態になる。
 */
export async function registerGrandparents({
  fatherId,
  motherId,
  grandparents,
  userId,
}: RegisterGrandparentsParams): Promise<void> {
  // 父方祖父母 → 父犬へ
  await linkGrandparents(
    fatherId,
    grandparents.paternalGrandfatherId,
    grandparents.paternalGrandmotherId,
    userId
  );

  // 母方祖父母 → 母犬へ
  await linkGrandparents(
    motherId,
    grandparents.maternalGrandfatherId,
    grandparents.maternalGrandmotherId,
    userId
  );
}
